import { Stack, TextField, Typography, InputAdornment } from '@mui/material';

import React, { useCallback, useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { useNavigate } from 'react-router-dom';

import { debounce } from 'lodash';
import moment from 'moment';

import {
	getExportColNames,
	getFilterHeaderValues,
	getFiltersValues,
} from 'common/api';
import { IFilterKeysParams, IFilterKeysResponse } from 'common/api/type-def';
import { ReactComponent as FilterIcon } from 'common/assets/images/filterwhite.svg';
import { ReactComponent as SearchOutlined } from 'common/assets/images/search-outline.svg';
import { ReactComponent as HorizonExport } from 'common/assets/images/vector-export.svg';
import ButtonComp from 'common/components/Button/ButtonComp';
import DatepickerComponent, {
	displayDateFormat,
} from 'common/components/Datepicker/Datepicker';
import ExportData from 'common/components/ExportData/ExportData';
import AdvancedFilters, {
	IAdvancedFilterValueOptions,
} from 'common/components/Filters/AdvancedFilters';
import { ISelectOption } from 'common/components/MulltipleSelectCheckmarks/MultipleSelectCheckmarks';
import { ISelectOption as ISelectDropdownOption } from 'common/components/SearchableSelectDropdown/SearchSelectDropdown';
import SideDrawer from 'common/components/SideDrawer/SideDrawer';
import { Pagination } from 'common/components/Table/CommonTableStyles';
import TableComponent from 'common/components/Table/Table';
import AlertComponent from 'common/components/ToastMessage/ToastMessage';
import { MODULES, MODULE_NAME, TABLE_CONSTANTS } from 'common/constants';
import { FeatureFlagsKeysEnum } from 'common/constants/FeatureFlags';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import { rrcpDashboardRoutes } from 'common/constants/rrcp-dashboard-routes';
import { APP_GENERAL_ERROR_MSG, PWC } from 'common/constants/TextContent';
import { RootState } from 'common/store/AppStore';
import {
	setHorizonScanningSearchText,
	setToastMessage,
} from 'common/store/reducer/commonReducer';
import {
	setHorizonAppliedFilterList,
	setHorizonApplyFilterEnabled,
	setHorizonDrawerState,
	setHorizonFilter,
	setHorizonFilterFlag,
	setHorizonFilterKeys,
	setHorizonSelectedAppliedFilterList,
	setIsNavigateToDetails,
	setShowAssignedToMe,
} from 'common/store/reducer/horizonScanReducer';
import { setWorkflowFeedback } from 'common/store/reducer/simulationReducer';
import {
	generateToastMessage,
	getAdvanceFilterPayloadData,
	getDateStringFromISOString,
	scrollToTop,
	urlWithTenant,
	filterDataHandler,
} from 'common/utils/utilFunctions';

import { useFeatureFlag } from 'hooks/useFeatureFlags';
import useTableQuery from 'hooks/useTableQuery';

import {
	DATE_ENTITY,
	HORIZON_PROGRESS_BAR_ELEMENTS,
} from 'features/HorizonScanning/HorizonScanConstants';
import {
	generatePayload,
	getExportColumnNames,
	handleClearFilter,
} from 'features/HorizonScanning/horizonScanUtils';
import {
	NO_COLUMN_ERROR_MESSAGE,
	TAB_VALUES,
} from 'features/RegulationInventory/RegInventoryUtils';
import { FilterParam } from 'features/RiskProfile/Taxonomy/RiskReferenceTaxonomy/RiskRefTaxonomy';
import { handleSpecialCharacters } from 'features/RiskProfile/Taxonomy/TaxonomyUtils';

import {
	addUserToTaskFlow,
	getUpdatesTableData,
	updateTaskWorkflow,
} from '../../api';
import {
	IApiLoading,
	IUpdatesTable,
	IUser,
	UpdateTableDataType,
	IUpdateTaskFlow,
	UpdateTableResponse,
	IExportColumnNames,
} from '../../api/type-def';

import AssignUserDropdown from '../AssignUserDropdown/AssignUserDropdown';

import ActivityFilter from './ActivityFilter';

import styles from './UpdatesTable.module.scss';

const { HORIZON_SCAN_AND_ALERTS } = MODULES;

const { OK } = HTTP_STATUS_CODES;

const { NOTSTARTED } = HORIZON_PROGRESS_BAR_ELEMENTS;

const { EXPORT } = TAB_VALUES;

const { NO_COLUMN_DATA } = NO_COLUMN_ERROR_MESSAGE;

export const getDateValue = (row: UpdateTableDataType) => {
	if (row?.taskId) {
		return row.reviewByDate;
	}
	return row.effectiveDate;
};

const UpdatesTable = (props: IUpdatesTable) => {
	const navigate = useNavigate();
	const { onSearchChange } = props;
	const { MODIFIED_DATE } = DATE_ENTITY;

	const { buttonTextColor, fillColorWhite, paddingStyle, exportDisabled } =
		styles;

	const [tableData, setTableData] = useState<UpdateTableResponse | undefined>();
	const [isLoading, setIsLoading] = useState(true);
	const [openExportModal, setOpenExportModal] = useState<boolean>(false);
	const [isExportDataLoading, setIsExportDataLoading] =
		useState<boolean>(false);
	const [exportColumnNames, setExportColumnNames] = useState<ISelectOption[]>(
		[],
	);
	const [, setAllFilterKeys] = useState<(string | null)[]>([]);
	const [selectedAssignedToMap, setSelectedAssignedToMap] = useState<{
		[key: string]: string;
	}>({});
	const [selectedDueDateMap, setSelectedDueDateMap] = useState<{
		[key: string]: string;
	}>({});
	const [, setAllFilterInitialData] = useState<unknown>([]);
	const [, setIsClearAllSelected] = useState(false);
	const [isApiLoading, setApiLoading] = useState<IApiLoading>({
		count: true,
		filters: false,
	});

	const searchText = useSelector(
		(state: RootState) => state.common.horizonScanningSearchText,
	);

	const isNavigateToDetails = useSelector(
		(state: RootState) => state.horizonScan.isNavigateToDetails,
	);

	const {
		IS_EXPORT_BUTTON_ENABLED,
		IS_HORIZON_SCANNING_ACTIVITY_FILTER_ENABLED,
	} = FeatureFlagsKeysEnum;

	const isLDResponseExists: boolean = useSelector(
		(state: RootState) => state.common.isLDResponseAvailable,
	);
	const IS_ACTIVITY_FILTER_ENABLED = useFeatureFlag(
		IS_HORIZON_SCANNING_ACTIVITY_FILTER_ENABLED,
		isLDResponseExists,
	);
	const isExportButtonEnabled = useFeatureFlag(
		IS_EXPORT_BUTTON_ENABLED,
		isLDResponseExists,
	);

	const dispatch = useDispatch();

	const { tableQueryObj, updateTableQuery } = useTableQuery({
		id: 'HorizonScanningUpdatesTable',
	});

	const navigateToDetail = (rowData: UpdateTableDataType) => {
		dispatch(setWorkflowFeedback([]));
		dispatch(setIsNavigateToDetails(true));
		navigate(
			urlWithTenant(
				`/${rrcpDashboardRoutes.horizonScanning}/${rrcpDashboardRoutes.horizonScanningDetail}/${rowData?.id}`,
			),
			{
				state: {
					routePath: MODULE_NAME.HORIZON_SCAN_NAME,
				},
			},
		);
	};

	const { isToastOpen, message, severity } = useSelector(
		(state: RootState) => state.common.toastMessage,
	);

	const horizonDrawerState = useSelector(
		(state: RootState) => state.horizonScan.horizonDrawerState,
	);

	const horizonFilterData = useSelector(
		(state: RootState) => state.horizonScan.horizonFilters,
	);

	const applyFilterEnabled = useSelector(
		(state: RootState) => state.horizonScan.horizonApplyFilterEnabled,
	);

	const horizonAppliedfilterKeys = useSelector(
		(state: RootState) => state.horizonScan.horizonAppliedfilterKeys,
	);
	const horizonSelectedAppliedfilterKeys = useSelector(
		(state: RootState) => state.horizonScan.horizonSelectedAppliedfilterKeys,
	);

	const dueDateDateValues = useSelector(
		(state: RootState) => state.horizonScan.dueDateValueRange,
	);

	const loggedInUser = useSelector(
		(state: RootState) => state.common.loggedInUser,
	);
	const showAssignedToMeRedux = useSelector(
		(state: RootState) => state.horizonScan.showAssignedToMe,
	);

	const [, setUpdatedFilterData] = useState<FilterParam[]>(horizonFilterData);

	const customDateDetailKeys = ['reviewByDate', 'effectiveDate'];
	const hideSearchFieldKeys = ['relevanceScore', 'taskStatus'];

	const handleClose = useCallback(() => {
		dispatch(setToastMessage({ isToastOpen: false }));
		dispatch(
			setToastMessage({
				message: '',
				isToastOpen: false,
				severity: '',
			}),
		);
	}, []);

	const handleSaveAssignedUser = async (
		selectedUser: ISelectDropdownOption,
		rowData: UpdateTableDataType,
		usersList: IUser[],
	) => {
		const obj = usersList?.find((user: IUser) => user.id === selectedUser?.id);
		setSelectedAssignedToMap((prevState) => ({
			...prevState,
			[selectedUser?.id]: selectedUser?.name,
		}));
		const payload = generatePayload(
			'assigedTo',
			selectedUser?.name,
			rowData,
			obj,
		);
		let updateResponse: any;
		if (rowData?.taskId) {
			updateResponse = await updateListOfUsersData(payload);
		} else {
			updateResponse = await addUserToTaskFlowData(payload);
		}
		if (updateResponse?.statusCode === OK) {
			const response = updateResponse.responseBody.taskResponseList;
			const updateTableData = tableData?.content.map((res) => {
				if (res.id === response[0].assignedObjectId) {
					res.assignedUserName = response[0].assignedUserFullName;
					res.taskStatus = response[0].status;
					res.taskId = response[0].id;
					return res;
				}
				return res;
			});
			const newObject = {
				...tableData,
				totalCount: tableData?.totalCount || 0,
				content: updateTableData || [],
				totalPages: tableData?.totalPages || 0,
				pageNumber: tableData?.pageNumber || 0,
				pageSize: tableData?.pageSize || 0,
			};
			setTableData(newObject);
		}
	};

	const handleDueDateChange = async (
		data: UpdateTableDataType,
		value: string,
	) => {
		setSelectedDueDateMap((prevState) => ({
			...prevState,
			[data.id]: value,
		}));
		const payload: IUpdateTaskFlow = generatePayload(
			'dueDate',
			value,
			data,
			null,
		);
		if (data.taskId) {
			await updateListOfUsersData(payload);
		} else {
			await addUserToTaskFlowData(payload);
		}
		await getTableData(
			{
				qs: searchText,
				filter: horizonFilterData,
				pagination: tableQueryObj,
			},
			true,
		);
	};

	useEffect(() => {
		scrollToTop();
		const paginationCondition = isNavigateToDetails
			? tableQueryObj
			: { ...tableQueryObj, pageNumber: 0 };

		updateTableQuery(paginationCondition);
		getTableData({
			qs: searchText,
			filter: horizonFilterData,
			pagination: paginationCondition,
		});
	}, [horizonFilterData, isNavigateToDetails]);

	const getAllFilterKeys = (data: IFilterKeysResponse[]) => {
		const filterKeys = data.map(
			(value: IFilterKeysResponse) => value?.filterKey,
		);
		const updatedFilteredKeys = filterKeys.filter(
			(element) => element !== null,
		);
		return updatedFilteredKeys;
	};

	const applyFiltersHandlerData = (data: IAdvancedFilterValueOptions[]) => {
		dispatch(setHorizonSelectedAppliedFilterList(data));

		const filteredArr = filterDataHandler(
			data,
			showAssignedToMeRedux,
			loggedInUser,
		);

		dispatch(setHorizonFilter(filteredArr));
		setUpdatedFilterData(filteredArr);
		dispatch(setHorizonApplyFilterEnabled(true));
		dispatch(setHorizonFilterFlag(true));
		dispatch(setHorizonDrawerState(false));
	};

	const getAllFilteredDropdownData = async (data: IFilterKeysResponse[]) => {
		const filtersData = data;
		const allKeys = getAllFilterKeys(filtersData);
		const updatedSearchString = handleSpecialCharacters(searchText);
		const paramsObj = {
			searchText: updatedSearchString,
			index: 'horizon-scanning',
			filter: horizonFilterData,
			relationship: allKeys,
			source: PWC,
		};
		const res = await getFiltersValues(paramsObj, HORIZON_SCAN_AND_ALERTS);
		if (res?.statusCode === OK) {
			const allFilteredData = res?.responseBody?.relationship;
			const allRelations = allFilteredData;

			getAdvanceFilterPayloadData(allRelations, filtersData);
			dispatch(setHorizonAppliedFilterList(allRelations));
			setAllFilterInitialData(allRelations);

			setApiLoading((previous) => ({ ...previous, filters: false }));
			dispatch(setHorizonFilterFlag(false));
			dispatch(setHorizonDrawerState(!horizonDrawerState));
		} else {
			setApiLoading((previous) => ({ ...previous, filters: false }));
			dispatch(setHorizonFilterFlag(false));
			setUpdatedFilterData([]);
		}
	};

	const onAdvanceFilter = async () => {
		setApiLoading((previous) => ({ ...previous, filters: true }));
		const params: IFilterKeysParams = {
			dataSource: PWC,
			module: 'horizon',
		};
		const res = await getFilterHeaderValues(params);
		if (res?.statusCode === OK && res?.responseBody) {
			setAllFilterInitialData(res?.responseBody);
			const allKeys = getAllFilterKeys(res?.responseBody);
			setAllFilterKeys(allKeys);
			dispatch(setHorizonFilterKeys(res?.responseBody));
			getAllFilteredDropdownData(res?.responseBody);
			dispatch(setHorizonDrawerState(true));
		} else {
			dispatch(
				setToastMessage({
					isToastOpen: true,
					severity: 'error',
					message: APP_GENERAL_ERROR_MSG,
				}),
			);
			setApiLoading((previous) => ({ ...previous, filters: false }));
		}
	};
	let filterCount = 0;
	// Only Activity Filter array from the horizonSelectedAppliedfilterKeys filterArray
	const onlyActivityFilterFromFilter = horizonSelectedAppliedfilterKeys?.filter(
		(item: { type: string }) => item.type === MODIFIED_DATE,
	);
	// excluding the Activity filter array from the horizonSelectedAppliedfilterKeys filterArray
	const excludeTheActivityFilterFromArray =
		horizonSelectedAppliedfilterKeys?.filter(
			(item: { type: string }) => item.type !== MODIFIED_DATE,
		);
	if (IS_ACTIVITY_FILTER_ENABLED) {
		filterCount = onlyActivityFilterFromFilter.length > 0 ? 1 : 0;
	}

	const isAnyFilterApplied =
		excludeTheActivityFilterFromArray.length > 0 ||
		showAssignedToMeRedux ||
		dueDateDateValues.length > 0;

	const columns = [
		{
			id: 'title',
			label: 'Title Name',
			disablePadding: false,
			render: (rowData: UpdateTableDataType) => (
				<Typography
					className={styles['table-title-name']}
					data-testid="title-link"
					onClick={() => navigateToDetail(rowData)}
				>
					{rowData.title}
				</Typography>
			),
		},
		{
			id: 'regulators',
			label: 'Regulators',
			disablePadding: false,
			isSortable: true,
			render: (rowData: UpdateTableDataType) => (
				<Typography className={styles['text-section-common']}>
					{rowData.regulators}
				</Typography>
			),
		},
		{
			id: 'assignedUserName',
			label: 'Assigned to',
			disablePadding: false,
			isSortable: true,
			render: (rowData: UpdateTableDataType) => (
				<Typography data-testId="assigned-to">
					{selectedAssignedToMap[rowData.id] || rowData.assignedUserName}
				</Typography>
			),
			isCellHoverEditable: true,
			renderHoverEditCell: (rowData: UpdateTableDataType) => (
				<AssignUserDropdown
					displayValue={
						rowData.assignedUserName
							? rowData.assignedUserName
							: selectedAssignedToMap[rowData.id]
					}
					rowData={rowData}
					saveButtonHandler={handleSaveAssignedUser}
				/>
			),
		},
		{
			id: 'reviewByDate',
			label: 'Due Date',
			disablePadding: false,
			isSortable: true,
			cellClassName: styles['due-date-styling'],
			render: (rowData: UpdateTableDataType) => (
				<Typography className={styles['text-section-common']}>
					{getDateValue(rowData)
						? moment(
								getDateStringFromISOString(getDateValue(rowData) ?? ''),
						  ).format(displayDateFormat)
						: ''}
				</Typography>
			),
			isCellHoverEditable: true,
			renderHoverEditCell: (rowData: UpdateTableDataType) => (
				<DatepickerComponent
					data-testId="due-date-picker"
					propType={{
						label: 'Due Date',
						disablePast: true,
						name: 'Review by',
						disabled: !rowData?.id,
						id: 'dueDate',
						required: true,
						inputProps: {
							'data-testid': 'reginv-dueDate',
						},
					}}
					showLabel={false}
					mode="standard"
					onChange={(value) => handleDueDateChange(rowData, value)}
					dateValue={
						selectedDueDateMap[rowData.id] ||
						(getDateStringFromISOString(getDateValue(rowData) ?? '') ??
							undefined)
					}
				/>
			),
		},
		{
			id: 'jurisdiction',
			label: 'Jurisdiction',
			disablePadding: false,
			isSortable: true,
		},
		{
			id: 'type',
			label: 'Type',
			disablePadding: false,
			isSortable: true,
		},
		{
			id: 'relatedObligationCount',
			label: 'Related Obligations',
			disablePadding: false,
		},
		{
			id: 'relevanceScore',
			label: 'AI Score',
			hideSentenceCase: true,
			disablePadding: false,
			render: (row: UpdateTableDataType) => (
				<div
					className={[
						row.relevanceScore !== 'Evaluating'
							? styles['ai-score'] && styles[row?.relevanceScore?.toLowerCase()]
							: styles.evaluating,
					].join(' ')}
				>
					{row?.relevanceScore || 'Evaluating'}
				</div>
			),
		},

		{
			id: 'taskStatus',
			label: 'Status',
			disablePadding: false,
			isSortable: true,
			render: (rowData: UpdateTableDataType) => (
				<Typography>
					{rowData?.taskStatus || rowData.status || NOTSTARTED}
				</Typography>
			),
		},
	];

	const getTableData = async (
		payload: {
			qs: string;
			filter: string[];
			pagination: Pagination;
		},
		showloader = true,
	) => {
		if (showloader) {
			setIsLoading(true);
		}
		const res = await getUpdatesTableData(
			{ searchText: payload.qs, filter: payload.filter },
			payload.pagination,
		);
		if (res?.statusCode === OK) {
			setTableData(res?.responseBody);
		}
		setIsLoading(false);
	};

	const handleAssignedToMeChecked = (isChecked: boolean) => {
		dispatch(setShowAssignedToMe(isChecked));
	};

	const updateListOfUsersData = async (payload: IUpdateTaskFlow) => {
		try {
			const res = await updateTaskWorkflow(payload);
			const toastMessageConfig = generateToastMessage(res);
			if (toastMessageConfig) {
				const { message, severity } = toastMessageConfig;
				dispatch(
					setToastMessage({
						message,
						isToastOpen: true,
						severity,
					}),
				);
			}
			return res;
		} catch (error) {
			return null;
		}
	};

	const addUserToTaskFlowData = async (payload: any) => {
		try {
			const res = await addUserToTaskFlow(payload);
			const toastMessageConfig = generateToastMessage(res);
			if (toastMessageConfig) {
				dispatch(
					setToastMessage({
						message: toastMessageConfig.message,
						isToastOpen: true,
						severity: toastMessageConfig.severity,
					}),
				);
			}
			return res;
		} catch (error) {
			return null;
		}
	};

	const handlePageChange = (data: Pagination, flag?: boolean) => {
		if (flag) {
			getTableData({
				qs: searchText,
				filter: horizonFilterData,
				pagination: data,
			});
			updateTableQuery(data);
		}
	};

	const handleSortChange = (data: Pagination) => {
		getTableData({
			qs: searchText,
			filter: horizonFilterData,
			pagination: data,
		});
	};

	const handleSearch = useCallback(
		debounce(async (searchQuery: string) => {
			const defaultPagination = {
				pageNumber: 0,
				pageSize: TABLE_CONSTANTS.customPageSize,
			};
			updateTableQuery(defaultPagination);
			await getTableData({
				qs: searchQuery,
				filter: horizonFilterData,
				pagination: defaultPagination,
			});
		}, 500),
		[horizonFilterData],
	);

	const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const searchStr = e.target.value;
		dispatch(setHorizonScanningSearchText(searchStr));
		onSearchChange?.(searchStr);
		handleSearch(searchStr);
	};

	const onCloseAdvancedFilter = () => {
		dispatch(setHorizonDrawerState(!horizonDrawerState));
	};

	const onCancelAdvancedFilter = () => {
		if (!applyFilterEnabled) {
			dispatch(setHorizonFilter([]));
			setUpdatedFilterData([]);
			dispatch(setHorizonApplyFilterEnabled(false));
			dispatch(setHorizonFilterFlag(false));
		}
		dispatch(setHorizonDrawerState(!horizonDrawerState));
	};

	const exportHandler = async () => {
		setIsExportDataLoading(true);
		const response = await getExportColNames('HORIZON_SCANNING_EXPORT_COLUMNS');
		if (response?.statusCode === OK) {
			setOpenExportModal(true);
			setExportColumnNames(
				getExportColumnNames(response?.responseBody as IExportColumnNames),
			);
		} else {
			dispatch(
				setToastMessage({
					isToastOpen: true,
					severity: 'error',
					message: NO_COLUMN_DATA,
				}),
			);
			onCloseHandler();
		}
		setIsExportDataLoading(false);
	};

	const onCloseHandler = () => {
		setOpenExportModal(false);
	};

	return (
		<Stack className={styles['table-container']} gap={2}>
			<>
				<Stack
					direction="row"
					justifyContent="space-between"
					alignItems="center"
				>
					<Typography className={styles.title}>Updates</Typography>
					<Stack gap={1} direction="row" alignItems="center">
						<TextField
							data-testId="table-search"
							value={searchText}
							onChange={handleSearchChange}
							InputProps={{
								sx: { paddingLeft: paddingStyle },
								startAdornment: (
									<InputAdornment position="start">
										<SearchOutlined className={styles['input-search-icon']} />
									</InputAdornment>
								),
							}}
							className={styles['search-input']}
							fullWidth={false}
							placeholder="Search"
							size="small"
						/>
						{IS_ACTIVITY_FILTER_ENABLED && <ActivityFilter />}
						{!isApiLoading?.filters ? (
							<ButtonComp
								btnType="secondary"
								data-testid="advancedfilters-icon"
								compClass={`${styles['advance-filter']} ${
									styles['search-input']
								} ${
									isAnyFilterApplied ? styles['advanced-filter-active'] : ''
								}`}
								clickHandler={onAdvanceFilter}
							>
								<div>
									<FilterIcon
										fill={isAnyFilterApplied ? fillColorWhite : buttonTextColor}
										className={styles['adv-filter-icon']}
									/>
								</div>
								<Typography className={styles['adv-filter-text']}>
									Advanced filters
								</Typography>
							</ButtonComp>
						) : (
							<ButtonComp
								btnType="secondary"
								data-testid="advancedfilters-loading-icon"
								compClass={`${styles['advance-filter']} ${styles['search-input']}`}
							>
								<Typography>Loading...</Typography>
							</ButtonComp>
						)}
						<ButtonComp
							disabled={
								tableData?.content.length === 0 || isExportButtonEnabled
							}
							compClass={styles['export-button']}
							clickHandler={exportHandler}
							data-testid="export-btn"
							btnType="secondary"
						>
							<div className={styles['export-icon']}>
								<HorizonExport
									fill={
										tableData?.content.length === 0 || isExportButtonEnabled
											? exportDisabled
											: buttonTextColor
									}
								/>
							</div>
							<Typography className={styles['export-text']}>
								{EXPORT}
							</Typography>
						</ButtonComp>
					</Stack>
				</Stack>
				<TableComponent
					defaultQuery={tableQueryObj}
					rows={tableData?.content || []}
					columns={columns}
					totalItems={tableData?.totalCount ?? 0}
					defaultSortKey="reviewByDate"
					defaultSortDirection="desc"
					rowHoverEnabled
					onPageHandler={handlePageChange}
					isLoading={isLoading}
					tableStyles={{ minWidth: 750, maxHeight: 440 }}
					onSortHandler={handleSortChange}
					enableHeaderColor
					isRenderPagination
				/>
				<AlertComponent
					open={isToastOpen ?? false}
					toastMessage={message ?? ''}
					severity={severity ?? ''}
					handleCloseMessage={handleClose}
					vertical="top"
					horizontal="right"
				/>
				{horizonDrawerState && (
					<SideDrawer
						className={styles['horizon-advancefilter-drawer']}
						drawerState={horizonDrawerState}
						customClass={styles['custom-width']}
					>
						<AdvancedFilters
							filterOptionsData={horizonAppliedfilterKeys}
							onClose={onCloseAdvancedFilter}
							applyFiltersHandler={applyFiltersHandlerData}
							handleClearFilter={() =>
								handleClearFilter(
									dispatch,
									setUpdatedFilterData,
									setIsClearAllSelected,
									applyFilterEnabled,
									onlyActivityFilterFromFilter,
								)
							}
							filterDataLoading={isApiLoading.filters}
							selectedListOptions={horizonSelectedAppliedfilterKeys}
							filterCount={filterCount}
							onCancel={onCancelAdvancedFilter}
							moduleType
							customDateDetailKeys={customDateDetailKeys}
							hideSearchFieldKeys={hideSearchFieldKeys}
							onAssignedToMeChecked={handleAssignedToMeChecked}
							showingAssignedToMe={showAssignedToMeRedux}
						/>
					</SideDrawer>
				)}
				{(openExportModal || isExportDataLoading) && (
					<ExportData
						onCloseModal={onCloseHandler}
						refFilterData={horizonFilterData}
						refSearchText={searchText}
						colNamesMapping={exportColumnNames}
						exportTypeName="horizon-scanning"
						optionsData={exportColumnNames}
						exportedCount={tableData?.totalCount || 0}
						moduleType={HORIZON_SCAN_AND_ALERTS}
						isDataLoading={isExportDataLoading}
					/>
				)}
			</>
		</Stack>
	);
};

export default UpdatesTable;
